import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  ArrowLeft, RefreshCw, CheckCircle, MessageSquare, FileSignature, 
  Clock, Loader2, User, Mail, Calendar, Bell, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProposalDealsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [activeTab, setActiveTab] = useState('open');
  
  const loadDeals = useCallback(async (status = 'all') => {
    setLoading(true);
    try {
      const token = localStorage.getItem('ats_token');
      const url = status === 'all' 
        ? `${API_URL}/api/proposal-deals` 
        : `${API_URL}/api/proposal-deals?status=${status}`;
      
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setDeals(data.deals || []);
      }
    } catch (e) {
      console.error('Error loading deals:', e);
      toast.error('Failed to load deals');
    }
    setLoading(false);
  }, []);
  
  useEffect(() => {
    loadDeals(activeTab);
  }, [activeTab, loadDeals]);
  
  const handleAction = async (dealId, action) => {
    setActionLoading(`${dealId}-${action}`);
    try {
      const token = localStorage.getItem('ats_token');
      const res = await fetch(`${API_URL}/api/proposal-deals/${dealId}/action`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action })
      });
      
      if (res.ok) {
        toast.success(`Action "${action}" completed`);
        loadDeals(activeTab);
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Action failed');
      }
    } catch (e) {
      toast.error('Action failed');
    }
    setActionLoading(null);
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const formatRelativeTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = date - now;
    const hours = Math.round(diff / (1000 * 60 * 60));
    const days = Math.round(diff / (1000 * 60 * 60 * 24));
    
    if (diff < 0) {
      return 'overdue';
    } else if (hours < 24) {
      return `in ${hours}h`;
    } else {
      return `in ${days}d`;
    }
  };
  
  const getChipBadge = (chip) => {
    switch (chip) {
      case 'reply':
        return <Badge className="bg-blue-100 text-blue-700 border-blue-200">Reply</Badge>;
      case 'signed':
        return <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Signed</Badge>;
      case 'snoozed':
        return <Badge className="bg-amber-100 text-amber-700 border-amber-200">Snoozed</Badge>;
      case 'needs_attention':
        return <Badge className="bg-red-100 text-red-700 border-red-200">Needs Attention</Badge>;
      default:
        return null;
    }
  };
  
  const filteredDeals = deals.filter(deal => {
    if (activeTab === 'open') return deal.status === 'open';
    if (activeTab === 'completed') return deal.status === 'completed';
    return true;
  });
  
  const DealCard = ({ deal }) => {
    const chips = deal.chips || [];
    const isSigned = chips.includes('signed');
    
    return (
      <Card className="mb-4" data-testid={`deal-card-${deal.id}`}>
        <CardContent className="pt-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="font-semibold text-slate-800 text-lg">
                {deal.recipient_name || deal.recipient_email}
              </div>
              <div className="text-sm text-slate-500 mt-1">
                <span className="font-medium">{deal.recipient_email}</span>
                <span className="mx-2">·</span>
                <span>{deal.subject}</span>
              </div>
              <div className="text-xs text-slate-400 mt-2 flex flex-wrap items-center gap-2">
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  Sent {formatDate(deal.sent_at)}
                </span>
                <span>·</span>
                <span className="flex items-center gap-1">
                  <Bell className="w-3 h-3" />
                  {deal.reminder_count} reminder(s)
                </span>
                {deal.status === 'open' && (
                  <>
                    <span>·</span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      next {formatRelativeTime(deal.next_reminder)}
                    </span>
                  </>
                )}
                <span>·</span>
                <span className="flex items-center gap-1">
                  <User className="w-3 h-3" />
                  by {deal.sender_name}
                </span>
              </div>
              
              {/* Status chips */}
              <div className="flex items-center gap-2 mt-3">
                <Badge variant={deal.status === 'open' ? 'default' : 'secondary'}>
                  {deal.status}
                </Badge>
                {chips.map((chip, idx) => (
                  <span key={idx}>{getChipBadge(chip)}</span>
                ))}
              </div>
            </div>
            
            {/* Actions */}
            {deal.status === 'open' && (
              <div className="flex flex-col gap-2 ml-4">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleAction(deal.id, 'complete')}
                  disabled={actionLoading === `${deal.id}-complete`}
                  className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                  data-testid={`deal-complete-${deal.id}`}
                >
                  {actionLoading === `${deal.id}-complete` ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Mark Complete
                    </>
                  )}
                </Button>
                
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleAction(deal.id, 'reply')}
                  disabled={actionLoading === `${deal.id}-reply`}
                  className="text-blue-600 border-blue-200 hover:bg-blue-50"
                  data-testid={`deal-reply-${deal.id}`}
                >
                  {actionLoading === `${deal.id}-reply` ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <MessageSquare className="w-4 h-4 mr-1" />
                      Reply Received
                    </>
                  )}
                </Button>
                
                {!isSigned && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleAction(deal.id, 'signed')}
                    disabled={actionLoading === `${deal.id}-signed`}
                    className="text-purple-600 border-purple-200 hover:bg-purple-50"
                    data-testid={`deal-signed-${deal.id}`}
                  >
                    {actionLoading === `${deal.id}-signed` ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <FileSignature className="w-4 h-4 mr-1" />
                        Agreement Signed
                      </>
                    )}
                  </Button>
                )}
                
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleAction(deal.id, 'snooze')}
                  disabled={actionLoading === `${deal.id}-snooze`}
                  className="text-amber-600 border-amber-200 hover:bg-amber-50"
                  data-testid={`deal-snooze-${deal.id}`}
                >
                  {actionLoading === `${deal.id}-snooze` ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Clock className="w-4 h-4 mr-1" />
                      Snooze 24h
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };
  
  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      <main className="max-w-5xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/ats/proposal-mailer')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-800">Deals</h1>
              <p className="text-slate-500">Track and follow up on sent proposals</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            onClick={() => loadDeals(activeTab)}
            disabled={loading}
            data-testid="refresh-deals-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="open" data-testid="deals-open-tab">
              Open
            </TabsTrigger>
            <TabsTrigger value="completed" data-testid="deals-completed-tab">
              Completed
            </TabsTrigger>
            <TabsTrigger value="all" data-testid="deals-all-tab">
              All
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value={activeTab}>
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : filteredDeals.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-slate-500">
                  <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No deals found</p>
                  <p className="text-sm mt-1">
                    Deals are created automatically when you send proposals
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div>
                {filteredDeals.map(deal => (
                  <DealCard key={deal.id} deal={deal} />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
